import { useState, useRef, useEffect } from "react";
import { useLanguage, LOCALE_NAMES, LOCALE_FLAGS, type Locale } from "@/contexts/LanguageContext";
import { Globe } from "lucide-react";

const locales: Locale[] = ["en", "de", "es", "it", "fr"];

export function LanguageSwitcher() {
  const { locale, setLocale } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-amber-100 hover:bg-white/10 transition-colors"
        aria-label="Switch language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{LOCALE_FLAGS[locale]}</span>
        <span className="hidden md:inline text-xs">{locale.toUpperCase()}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-[#1a2e1a]/95 backdrop-blur-md border border-amber-600/30 rounded-xl shadow-2xl overflow-hidden z-50 min-w-[160px]">
          {locales.map((l) => (
            <button
              key={l}
              onClick={() => { setLocale(l); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                l === locale
                  ? "bg-amber-600/20 text-amber-300 font-semibold"
                  : "text-amber-100/80 hover:bg-white/5 hover:text-amber-100"
              }`}
            >
              <span className="text-base">{LOCALE_FLAGS[l]}</span>
              <span>{LOCALE_NAMES[l]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
